import React, { useState, useEffect, useMemo } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import './GuidedTutorial.css';

interface GuidedTutorialProps {
  isActive: boolean;
  onClose: () => void;
  onHighlightElement?: (elementId: string | null) => void;
  currentStep?: number;
  onStepChange?: (step: number) => void;
  onAction?: (action: string) => void; // Pour déclencher des actions (ouvrir menu, etc.)
}

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  highlightElement?: string; // ID de l'élément à mettre en évidence
  action?: string; // Identifiant de l'action à effectuer pour cette étape
}

const GuidedTutorial: React.FC<GuidedTutorialProps> = ({ 
  isActive, 
  onClose,
  onHighlightElement,
  currentStep: externalStep,
  onStepChange,
  onAction
}) => {
  const { t } = usePageTranslation('game', 'GuidedTutorial');
  const [internalStep, setInternalStep] = useState(0);
  const currentStep = externalStep !== undefined ? externalStep : internalStep;

  const steps = useMemo<TutorialStep[]>(() => [
    {
      id: 'welcome',
      title: t('steps.welcome.title'),
      description: t('steps.welcome.description'),
    },
    {
      id: 'map',
      title: t('steps.map.title'),
      description: t('steps.map.description'),
    },
    {
      id: 'sidebar',
      title: t('steps.sidebar.title'),
      description: t('steps.sidebar.description'),
      highlightElement: 'sidebar-menu-button',
      action: 'open-sidebar' as string,
    },
    {
      id: 'buildings',
      title: t('steps.buildings.title'),
      description: t('steps.buildings.description'),
      highlightElement: 'buildings-menu-item',
      action: 'open-buildings' as string,
    },
    {
      id: 'context-menu',
      title: t('steps.contextMenu.title'),
      description: t('steps.contextMenu.description'),
    },
    {
      id: 'formation',
      title: t('steps.formation.title'),
      description: t('steps.formation.description'),
      highlightElement: 'formation-menu-item',
    },
    {
      id: 'complete',
      title: t('steps.complete.title'),
      description: t('steps.complete.description'),
    },
  ], [t]);

  useEffect(() => {
    if (isActive) {
      const step = steps[currentStep];
      
      // Mettre en évidence l'élément
      if (onHighlightElement) {
        onHighlightElement(step.highlightElement || null);
      }
      
      // Déclencher les actions automatiques
      if (step.action && onAction) {
        // Petit délai pour que l'interface se mette à jour
        setTimeout(() => {
          onAction(step.action!);
        }, 300);
      }
    }
  }, [currentStep, isActive, onHighlightElement, onAction, steps]);

  const handleNext = () => {
    const nextStep = currentStep + 1;
    if (nextStep < steps.length) {
      if (onStepChange) {
        onStepChange(nextStep);
      } else {
        setInternalStep(nextStep);
      }
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      if (onStepChange) {
        onStepChange(prevStep);
      } else {
        setInternalStep(prevStep);
      }
    }
  };

  const handleFinish = () => {
    // Ne plus sauvegarder la complétion pour permettre de refaire le tutoriel
    // Le tutoriel peut être refait à chaque fois qu'il est activé
    if (onHighlightElement) {
      onHighlightElement(null);
    }
    onClose();
  };

  const handleSkip = () => {
    handleFinish();
  };

  // Debug: vérifier si le tutoriel devrait s'afficher
  useEffect(() => {
    if (isActive) {
      console.log('🎓 GuidedTutorial: isActive = true, affichage du tutoriel');
    } else {
      console.log('🎓 GuidedTutorial: isActive = false, tutoriel masqué');
    }
  }, [isActive]);

  if (!isActive) {
    return null;
  }

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* Overlay sombre avec effet de flou */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      {/* Contenu du tutoriel */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
        <div className="bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full mx-4 border-2 border-primary-500">
          {/* En-tête */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('title')}</h3>
                  <p className="text-sm text-primary-100">
                    {t('stepIndicator')?.replace('{{current}}', String(currentStep + 1))?.replace('{{total}}', String(steps.length)) || `Étape ${currentStep + 1} sur ${steps.length}`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleSkip}
                className="text-white/80 hover:text-white transition-colors"
                aria-label={t('skip')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Corps */}
          <div className="p-6">
            <h4 className="text-2xl font-bold text-white mb-3">{currentStepData.title}</h4>
            <p className="text-gray-300 leading-relaxed mb-6 whitespace-pre-line">{currentStepData.description}</p>
            
            {/* Indicateur visuel pour les éléments à mettre en évidence */}
            {currentStepData.highlightElement && (
              <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-200 text-sm">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                  <span>{t('lookForHighlight')}</span>
                </div>
              </div>
            )}

            {/* Indicateur de progression */}
            <div className="mb-6">
              <div className="flex gap-2">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 flex-1 rounded-full transition-all ${
                      index === currentStep
                        ? 'bg-primary-500'
                        : index < currentStep
                        ? 'bg-primary-300'
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Boutons de navigation */}
            <div className="flex gap-3">
              {!isFirstStep && (
                <button
                  onClick={handlePrevious}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                >
                  {t('previous')}
                </button>
              )}
              <button
                onClick={handleNext}
                className={`flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium ${
                  isFirstStep ? 'w-full' : ''
                }`}
              >
                {isLastStep ? t('finish') : t('next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTutorial;

